import React from "react";
import {
  BrowserRouter,
  Routes as RouterRoutes,
  Route,
  Navigate,
} from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { AuthProvider } from "contexts/AuthContext";
import ProtectedRoute from "components/ProtectedRoute";
import NotFound from "pages/NotFound";
import CustomerSurvey from "./pages/customer-survey";
import CustomerDatabase from "./pages/customer-database";
import CustomerDetail from "./pages/customer-detail";
import Login from "./pages/login";
import HostDashboard from "./pages/admin-dashboard";
import InstallationWorkflow from "./pages/installation-workflow";
import ClusterManagement from "./pages/cluster-management";
import RegionManagement from "./pages/region-management";
import ClusterDashboard from "./pages/cluster-dashboard";
import CommissioningWorkflow from "./pages/commissioning-workflow";
import TransportWorkflow from "./pages/transport-workflow";
import WorkerDashboard from "./pages/worker-dashboard";
import Approvals from "./pages/approvals";
import Unworkable from "./pages/unworkable";
import Unauthorized from "./pages/Unauthorized";
import Admin from "./pages/admin";
import SurveyReportPublic from "./pages/survey-report-public";
import TechnicalReportPublic from "./pages/technical-report-public";
import WorkCompletionReportPublic from "./pages/work-completion-report-public";

const Routes = () => {
  return (
    <BrowserRouter>
      <ErrorBoundary>
        <AuthProvider>
          <ScrollToTop />
          <RouterRoutes>
            {/* Public routes */}
            <Route path="/login" element={<Login />} />
            <Route path="/" element={<Navigate to="/login" replace />} />

            {/* Unauthorized page */}
            <Route path="/unauthorized" element={<Unauthorized />} />

            {/* Public survey report download (signed link from export) - no auth */}
            <Route
              path="/survey-report/:token"
              element={<SurveyReportPublic />}
            />

            {/* Public technical report view (signed link from export) - no auth */}
            <Route
              path="/technical-report/:token"
              element={<TechnicalReportPublic />}
            />

            {/* Public work completion report download (signed link from export) - no auth */}
            <Route
              path="/work-completion-report/:token"
              element={<WorkCompletionReportPublic />}
            />

            {/* Protected routes - Super Admin (full access) */}
            <Route
              path="/host-dashboard"
              element={
                <ProtectedRoute
                  requiredRoles={["super_admin", "project_admin"]}
                >
                  <HostDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-database"
              element={
                <ProtectedRoute
                  requiredRoles={[
                    "super_admin",
                    "project_admin",
                    "cluster_manager",
                  ]}
                >
                  <CustomerDatabase />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <ProtectedRoute
                  requiredRoles={[
                    "super_admin",
                    "project_admin",
                    "cluster_manager",
                  ]}
                >
                  <Approvals />
                </ProtectedRoute>
              }
            />
            <Route
              path="/unworkable"
              element={
                <ProtectedRoute
                  requiredRoles={[
                    "super_admin",
                    "project_admin",
                    "cluster_manager",
                  ]}
                >
                  <Unworkable />
                </ProtectedRoute>
              }
            />
            <Route
              path="/customer-detail/:customerId"
              element={
                <ProtectedRoute>
                  <CustomerDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/region-management"
              element={
                <ProtectedRoute
                  requiredRoles={["super_admin", "project_admin"]}
                >
                  <RegionManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/cluster-management"
              element={
                <ProtectedRoute
                  requiredRoles={["super_admin", "project_admin"]}
                >
                  <ClusterManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/admin"
              element={
                <ProtectedRoute requiredRoles={["super_admin"]}>
                  <Admin />
                </ProtectedRoute>
              }
            />

            {/* Protected routes - Cluster Manager */}
            <Route
              path="/cluster-dashboard"
              element={
                <ProtectedRoute requiredRoles={["cluster_manager"]}>
                  <ClusterDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/customer-survey"
              element={
                <ProtectedRoute requiredRoles={["worker"]}>
                  <CustomerSurvey />
                </ProtectedRoute>
              }
            />

            <Route
              path="/worker-dashboard"
              element={
                <ProtectedRoute requiredRoles={["worker"]}>
                  <WorkerDashboard />
                </ProtectedRoute>
              }
            />

            <Route
              path="/transport-workflow"
              element={
                <ProtectedRoute requiredRoles={["worker"]}>
                  <TransportWorkflow />
                </ProtectedRoute>
              }
            />
            <Route
              path="/commissioning-workflow"
              element={
                <ProtectedRoute requiredRoles={["worker"]}>
                  <CommissioningWorkflow />
                </ProtectedRoute>
              }
            />

            {/* Installation workflow - accessible by civil and technical */}
            <Route
              path="/installation-workflow"
              element={
                <ProtectedRoute requiredRoles={["worker"]}>
                  <InstallationWorkflow />
                </ProtectedRoute>
              }
            />

            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

export default Routes;
